//
// # AddLayoutPinDialog
//
// Modal dialog for configuring a new layout pin before placement.
// Fields: Name, Layer (from PDK), Pin Type (signal/power/ground/clock).
//

import React, { useState, useCallback } from "react";
import { Modal } from "./Modal/Modal";
import { schema } from "GenAlphaSchema";

// Derive pin type options from the schema PinType constant.
const PIN_TYPE_OPTIONS = Object.entries(schema.core.PinType).map(
  ([label, value]) => ({ value, label })
);

// The result returned when the user confirms the dialog.
export interface LayoutPinConfig {
  readonly name: string;
  readonly layer: number;
  readonly pinType: schema.core.PinType;
}

interface AddLayoutPinDialogProps {
  readonly layers: ReadonlyArray<{
    readonly name: string;
    readonly number: number;
  }>;
  readonly onConfirm: (config: LayoutPinConfig) => void;
  readonly onCancel: () => void;
}

export function AddLayoutPinDialog({
  layers,
  onConfirm,
  onCancel,
}: AddLayoutPinDialogProps) {
  const [name, setName] = useState("pin0");
  const defaultLayer = layers.length > 0 ? layers[0].number : 0;
  const [layer, setLayer] = useState<number>(defaultLayer);
  const [pinType, setPinType] = useState<schema.core.PinType>("signal");

  const handleConfirm = useCallback(() => {
    onConfirm({ name, layer, pinType });
  }, [name, layer, pinType, onConfirm]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleConfirm();
      }
    },
    [handleConfirm]
  );

  const selectStyle = {
    backgroundColor: "var(--color-ide-surface-2)",
    borderColor: "var(--color-ide-border)",
    color: "var(--color-ide-text)",
  };

  const inputStyle = {
    backgroundColor: "var(--color-ide-surface-2)",
    borderColor: "var(--color-ide-border)",
    color: "var(--color-ide-text)",
  };

  return (
    <Modal open={true} onClose={onCancel} title="Add Layout Pin">
      <div onKeyDown={handleKeyDown} className="flex flex-col gap-3">
        {/* Pin Name */}
        <div className="flex flex-col gap-1">
          <label
            className="text-[12px]"
            style={{ color: "var(--color-ide-text-muted)" }}
          >
            Name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            className="px-2 py-1.5 text-[13px] rounded border outline-none"
            style={inputStyle}
          />
        </div>

        {/* Layer */}
        <div className="flex flex-col gap-1">
          <label
            className="text-[12px]"
            style={{ color: "var(--color-ide-text-muted)" }}
          >
            Layer
          </label>
          <select
            value={layer}
            onChange={(e) => setLayer(Number(e.target.value))}
            className="px-2 py-1.5 text-[13px] rounded border outline-none"
            style={selectStyle}
          >
            {layers.map((l) => (
              <option key={l.number} value={l.number}>
                {l.name}
              </option>
            ))}
          </select>
        </div>

        {/* Pin Type */}
        <div className="flex flex-col gap-1">
          <label
            className="text-[12px]"
            style={{ color: "var(--color-ide-text-muted)" }}
          >
            Pin Type
          </label>
          <select
            value={pinType}
            onChange={(e) => setPinType(e.target.value as schema.core.PinType)}
            className="px-2 py-1.5 text-[13px] rounded border outline-none"
            style={selectStyle}
          >
            {PIN_TYPE_OPTIONS.map((pt) => (
              <option key={pt.value} value={pt.value}>
                {pt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-2 mt-3">
          <button
            onClick={onCancel}
            className="px-4 py-1.5 text-[13px] rounded border cursor-pointer bg-transparent"
            style={{
              borderColor: "var(--color-ide-border)",
              color: "var(--color-ide-text)",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="px-4 py-1.5 text-[13px] rounded border-none cursor-pointer"
            style={{
              backgroundColor: "var(--color-ide-accent)",
              color: "#ffffff",
            }}
          >
            Add Pin
          </button>
        </div>
      </div>
    </Modal>
  );
}
